import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Eye, Paperclip, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Attachments } from "@/components/Attachments";
import EditRowSheet from "@/components/EditRowSheet";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

export default function Meetings({ projectId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const empty = {
    project_id: projectId || "", title: "", meeting_date: new Date().toISOString().slice(0, 10),
    location: "", attendeesCsv: "",
    agenda: "", discussion: "", decisions: "", notes: "",
    next_actions: [{ action: "", owner: "", due_date: "" }],
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => Promise.all([
    api.get("/meetings", { params: projectId ? { project_id: projectId } : {} }),
    api.get("/projects"),
  ]).then(([a, b]) => { setRows(a.data); setProjects(b.data); }), [projectId]);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  const submit = async () => {
    try {
      await api.post("/meetings", {
        ...form,
        project_id: projectId || form.project_id || null,
        attendees: form.attendeesCsv.split(",").map((s) => s.trim()).filter(Boolean),
        next_actions: form.next_actions.filter((a) => a.action),
      });
      toast.success("Meeting saved");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this meeting record?")) return;
    try { await api.delete(`/meetings/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const setAction = (idx, patch) => setForm((f) => ({
    ...f, next_actions: f.next_actions.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
  }));

  return (
    <div data-testid="meetings-page">
      {!embedded && (
      <PageHeader
        title="Meetings & MOM"
        subtitle="Minutes of meeting for project reviews, kickoffs and site walks."
        actions={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="create-meeting-button" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
                <Plus size={16} className="mr-2" /> New MOM
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[560px] sm:max-w-xl overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display">Record Minutes of Meeting</SheetTitle></SheetHeader>
              <MeetingFormBody form={form} setForm={setForm} projects={projects} projectId={projectId} submit={submit} />
            </SheetContent>
          </Sheet>
        }
      />
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">Meetings & MOM ({rows.length})</div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button size="sm" data-testid="create-meeting-button" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-8">
                <Plus size={14} className="mr-1" /> New MOM
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[560px] sm:max-w-xl overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display">Record Minutes of Meeting</SheetTitle></SheetHeader>
              <MeetingFormBody form={form} setForm={setForm} projects={projects} projectId={projectId} submit={submit} />
            </SheetContent>
          </Sheet>
        </div>
      )}

      <div className={embedded ? "" : "p-8"}>
        {rows.length === 0 ? (
          <EmptyState title="No meetings yet" description="Record your first MOM — attendees, decisions and next-action owners are searchable later." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="meetings-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Date</TableHead>
                  <TableHead className="label-caps">Title</TableHead>
                  <TableHead className="label-caps">Project</TableHead>
                  <TableHead className="label-caps">Attendees</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m.id} data-testid={`meeting-row-${m.id}`}>
                    <TableCell className="text-slate-600 font-mono-num">{fmtDate(m.meeting_date)}</TableCell>
                    <TableCell className="font-medium">{m.title}</TableCell>
                    <TableCell className="text-slate-600">{projMap[m.project_id] || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{(m.attendees || []).join(", ") || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setViewing(m)} data-testid={`view-meeting-${m.id}`} className="text-slate-500 hover:text-[#EA580C] p-1"><Eye size={14} /></button>
                        <button onClick={() => setEditing(m)} data-testid={`edit-meeting-${m.id}`} title="Edit" className="text-slate-500 hover:text-[#EA580C] p-1"><PencilSimple size={14} /></button>
                        <button onClick={() => del(m.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {editing && (
        <EditRowSheet
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          title="Edit Meeting"
          endpoint={`/meetings/${editing.id}`}
          initial={{
            ...editing,
            attendeesCsv: Array.isArray(editing.attendees) ? editing.attendees.join(", ") : "",
          }}
          fields={[
            { name: "title", label: "Title", required: true, colSpan: 2 },
            { name: "meeting_date", label: "Date", type: "date", required: true },
            { name: "location", label: "Location" },
            { name: "attendeesCsv", label: "Attendees (comma-separated)", colSpan: 2 },
            { name: "agenda", label: "Agenda", type: "textarea", colSpan: 2 },
            { name: "discussion", label: "Discussion", type: "textarea", colSpan: 2 },
            { name: "decisions", label: "Decisions", type: "textarea", colSpan: 2 },
            { name: "notes", label: "Notes", type: "textarea", colSpan: 2 },
          ]}
          transformPayload={(f) => {
            const { attendeesCsv, ...rest } = f;
            return {
              ...rest,
              attendees: (attendeesCsv || "").split(",").map((s) => s.trim()).filter(Boolean),
            };
          }}
          onSaved={load}
        />
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl overflow-y-auto max-h-[90vh]" data-testid="meeting-viewer">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{viewing.title}</DialogTitle>
                <div className="text-sm text-slate-500 mt-1">{fmtDate(viewing.meeting_date)}{viewing.location ? ` · ${viewing.location}` : ""}</div>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <Section label="Attendees">{(viewing.attendees || []).join(", ") || "—"}</Section>
                {viewing.agenda && <Section label="Agenda">{viewing.agenda}</Section>}
                {viewing.discussion && <Section label="Discussion">{viewing.discussion}</Section>}
                {viewing.decisions && <Section label="Decisions">{viewing.decisions}</Section>}
                {(viewing.next_actions || []).length > 0 && (
                  <div>
                    <div className="label-caps mb-2">Next actions</div>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50"><tr>
                        <th className="p-2 text-left label-caps">Action</th>
                        <th className="p-2 text-left label-caps">Owner</th>
                        <th className="p-2 text-left label-caps">Due</th>
                      </tr></thead>
                      <tbody>{viewing.next_actions.map((a, i) => (
                        <tr key={`${viewing.id}-action-${i}-${a.action || ""}`} className="border-t border-slate-100">
                          <td className="p-2">{a.action}</td>
                          <td className="p-2 text-slate-600">{a.owner}</td>
                          <td className="p-2 text-slate-600">{fmtDate(a.due_date)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
                {viewing.notes && <Section label="Notes">{viewing.notes}</Section>}
                <div className="pt-4 border-t border-slate-200">
                  <div className="label-caps mb-2 flex items-center gap-1"><Paperclip size={12} /> Files</div>
                  <Attachments parentType="meeting" parentId={viewing.id} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="label-caps mb-1">{label}</div>
      <div className="text-slate-700 whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function MeetingFormBody({ form, setForm, projects, projectId, submit }) {
  const setAction = (idx, patch) => setForm((f) => ({
    ...f, next_actions: f.next_actions.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
  }));
  return (
    <div className="space-y-4 mt-6">
      <Field label="Title"><Input data-testid="meeting-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Kickoff — Realty Corp Phase 1" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Meeting date"><Input type="date" data-testid="meeting-date-input" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} /></Field>
        <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Site / Zoom / Client office" /></Field>
      </div>
      {!projectId && (
        <Field label="Project (optional)">
          <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      )}
      <Field label="Attendees (comma-separated)"><Input data-testid="meeting-attendees-input" value={form.attendeesCsv} onChange={(e) => setForm({ ...form, attendeesCsv: e.target.value })} placeholder="Ravi Kumar, Aditi Sharma, Mr. Client" /></Field>
      <Field label="Agenda"><Textarea rows={2} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} /></Field>
      <Field label="Discussion"><Textarea rows={3} value={form.discussion} onChange={(e) => setForm({ ...form, discussion: e.target.value })} /></Field>
      <Field label="Decisions"><Textarea rows={2} value={form.decisions} onChange={(e) => setForm({ ...form, decisions: e.target.value })} /></Field>
      <div>
        <Label className="label-caps mb-2 block">Next actions</Label>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_120px_120px_24px] gap-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500">
            <span>Action</span><span>Owner</span><span>Due</span><span></span>
          </div>
          {form.next_actions.map((a, idx) => (
            <div key={`na-${idx}`} className="grid grid-cols-[1fr_120px_120px_24px] gap-2 items-center">
              <Input value={a.action} onChange={(e) => setAction(idx, { action: e.target.value })} />
              <Input value={a.owner} onChange={(e) => setAction(idx, { owner: e.target.value })} />
              <Input type="date" value={a.due_date} onChange={(e) => setAction(idx, { due_date: e.target.value })} />
              <button onClick={() => setForm({ ...form, next_actions: form.next_actions.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
            </div>
          ))}
          <button onClick={() => setForm({ ...form, next_actions: [...form.next_actions, { action: "", owner: "", due_date: "" }] })} className="text-xs text-[#EA580C] font-semibold">+ ADD ACTION</button>
        </div>
      </div>
      <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      <Button onClick={submit} data-testid="save-meeting-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Save MOM</Button>
    </div>
  );
}
